import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader } from '@/components/ui/card'
import { useTRPC } from '@/lib/trpc'
import { getPlayerCountFromGameSetting } from '@repo/sdk'
import { useQuery } from '@tanstack/react-query'
import { Fragment, useEffect, useState } from 'react'
import {
  Breadcrumbs,
  EmailStatus,
  InternalLink,
  Metric,
  ParticipantStatus,
  QueryNotice,
  RefreshButton,
  StatusBadge,
  categoryLabel,
  roleLabel,
  useBoundaryRefresh,
  useTournamentCopy,
  utcDate,
} from './shared'
import type { Copy, Navigate, Navigation, RecordEntry } from './shared'

function RecordStatus({ record, copy }: { record: RecordEntry, copy: Copy }) {
  if (record.state === 'IN_PROGRESS')
    return <StatusBadge label={copy('active')} tone="active" />
  if (record.state === 'FORFEITED_LOSS')
    return <StatusBadge label={copy('forfeited')} tone="negative" />
  if (record.state === 'VOID')
    return <StatusBadge label={copy('void')} />
  return <StatusBadge label={copy('complete')} tone="positive" />
}

function RecordResult({ record, copy }: { record: RecordEntry, copy: Copy }) {
  if (record.state === 'COMPLETED_WIN')
    return <StatusBadge label={copy('win')} tone="positive" />
  if (record.state === 'COMPLETED_LOSS' || record.state === 'FORFEITED_LOSS')
    return <StatusBadge label={copy('loss')} tone="negative" />
  return <StatusBadge label={copy(record.state === 'VOID' ? 'void' : 'pending')} />
}

function RecordMetadata({ record, copy, id }: { record: RecordEntry, copy: Copy, id: string }) {
  const seats = getPlayerCountFromGameSetting(record.game.gameSetting) ?? record.game.players
  return (
    <div id={id} className="rounded-lg border border-border bg-muted/50 p-4">
      <dl className="grid gap-4 text-sm sm:grid-cols-3">
        <div className="min-w-0"><dt className="text-xs text-muted-foreground">{copy('board')}</dt><dd className="mt-1 break-all">{copy('boardValue', { seats, boardName: record.game.gameSetting })}</dd></div>
        <div><dt className="text-xs text-muted-foreground">{copy('sheriff')}</dt><dd className="mt-1">{record.game.hasSheriff ? copy('sheriff') : '—'}</dd></div>
        <div><dt className="text-xs text-muted-foreground">{copy('status')}</dt><dd className="mt-1"><RecordStatus record={record} copy={copy} /></dd>{record.retryLater && <p className="mt-2 text-xs text-muted-foreground">{copy('retryLater')}</p>}</div>
      </dl>
    </div>
  )
}

function RecordRows({ records, expanded, toggle, copy }: {
  records: RecordEntry[]
  expanded: ReadonlySet<string>
  toggle: (id: string) => void
  copy: Copy
}) {
  return (
    <>
      <div className="hidden overflow-x-auto lg:block">
        <table className="w-full table-fixed text-xs">
          <caption className="sr-only">{copy('playerRecords')}</caption>
          <thead><tr className="border-b bg-muted/50 text-left text-muted-foreground"><th scope="col" className="w-[6%] px-2 py-3 font-medium">{copy('gameNumber')}</th><th scope="col" className="w-[20%] px-2 py-3 font-medium">{copy('gameId')}</th><th scope="col" className="w-[10%] px-2 py-3 font-medium">{copy('role')}</th><th scope="col" className="w-[17%] px-2 py-3 font-medium">{copy('status')}</th><th scope="col" className="w-[9%] px-2 py-3 font-medium">{copy('result')}</th><th scope="col" className="w-[13%] px-2 py-3 font-medium">{copy('startedAt')}</th><th scope="col" className="w-[13%] px-2 py-3 font-medium">{copy('settledAt')}</th><th scope="col" className="w-[12%] px-2 py-3 font-medium">{copy('action')}</th></tr></thead>
          <tbody>{records.map((record, index) => {
            const open = expanded.has(record.id)
            const detailId = `tournament-record-desktop-${record.id}`
            return (
              <Fragment key={record.id}>
                <tr className="border-b hover:bg-muted/30">
                  <td className="px-2 py-4 tabular-nums">{index + 1}</td>
                  <td className="break-all px-2 py-4 font-mono">{record.gameId}</td>
                  <td className="px-2 py-4">{roleLabel(record.role, copy)}</td>
                  <td className="px-2 py-4"><RecordStatus record={record} copy={copy} />{record.retryLater && <p className="mt-2 text-muted-foreground">{copy('retryLater')}</p>}</td>
                  <td className="px-2 py-4"><RecordResult record={record} copy={copy} /></td>
                  <td className="px-2 py-4 tabular-nums">{utcDate(record.createdAt)}</td>
                  <td className="px-2 py-4 tabular-nums">{utcDate(record.settledAt)}</td>
                  <td className="px-2 py-4"><Button type="button" variant="outline" size="sm" className="min-h-11" aria-expanded={open} aria-controls={detailId} onClick={() => toggle(record.id)}>{copy(open ? 'hideDetails' : 'details')}</Button></td>
                </tr>
                {open && <tr className="border-b"><td colSpan={8} className="px-2 py-4"><RecordMetadata record={record} copy={copy} id={detailId} /></td></tr>}
              </Fragment>
            )
          })}
          </tbody>
        </table>
      </div>
      <div className="space-y-3 lg:hidden">{records.map((record, index) => {
        const open = expanded.has(record.id)
        const detailId = `tournament-record-mobile-${record.id}`
        return (
          <article key={record.id} className="min-w-0 rounded-lg border border-border p-4">
            <div className="mb-4 flex flex-wrap items-start justify-between gap-3"><h4 className="font-semibold">#{index + 1} · {roleLabel(record.role, copy)}</h4><RecordStatus record={record} copy={copy} /></div>
            <dl className="grid grid-cols-2 gap-4 text-sm">
              <div className="col-span-2"><dt className="text-xs text-muted-foreground">{copy('gameId')}</dt><dd className="mt-1 break-all font-mono text-xs">{record.gameId}</dd></div>
              <div className="col-span-2"><dt className="text-xs text-muted-foreground">{copy('result')}</dt><dd className="mt-1"><RecordResult record={record} copy={copy} /></dd></div>
              <div><dt className="text-xs text-muted-foreground">{copy('startedAt')}</dt><dd className="mt-1 text-xs tabular-nums">{utcDate(record.createdAt)}</dd></div>
              <div><dt className="text-xs text-muted-foreground">{copy('settledAt')}</dt><dd className="mt-1 text-xs tabular-nums">{utcDate(record.settledAt)}</dd></div>
            </dl>
            {record.retryLater && <p className="mt-3 text-xs text-muted-foreground">{copy('retryLater')}</p>}
            <Button type="button" variant="outline" className="mt-4 min-h-11 w-full" aria-expanded={open} aria-controls={detailId} onClick={() => toggle(record.id)}>{copy(open ? 'hideDetails' : 'details')}</Button>
            {open && <div className="mt-4"><RecordMetadata record={record} copy={copy} id={detailId} /></div>}
          </article>
        )
      })}
      </div>
    </>
  )
}

export function ParticipantView({ navigation, navigate }: { navigation: Navigation, navigate: Navigate }) {
  const { copy, language } = useTournamentCopy()
  const trpc = useTRPC()
  const [expanded, setExpanded] = useState<Set<string>>(() => new Set())
  useEffect(() => setExpanded(new Set()), [navigation.editionId, navigation.participantId])
  const query = useQuery({
    ...trpc.tournamentRouter.adminGetParticipant.queryOptions({ editionId: navigation.editionId!, userId: navigation.participantId!, language }),
    refetchInterval: 15_000,
    refetchOnWindowFocus: true,
    refetchOnMount: 'always',
  })
  const { data } = query
  useBoundaryRefresh(data?.edition.lifecycle, query.dataUpdatedAt, query.refetch)
  const records = data ? [...data.records].sort((a, b) => a.createdAt.localeCompare(b.createdAt) || a.id.localeCompare(b.id)) : []
  const toggle = (id: string) => setExpanded((current) => {
    const next = new Set(current)
    if (next.has(id))
      next.delete(id)
    else
      next.add(id)
    return next
  })

  return (
    <div className="space-y-6" aria-busy={query.isFetching}>
      <div><Breadcrumbs navigation={navigation} navigate={navigate} edition={data?.edition} /><div className="flex flex-wrap items-start justify-between gap-4"><div><h2 className="text-2xl font-semibold tracking-tight">{copy('participantView')}</h2>{data && <p className="mt-2 text-sm text-muted-foreground">{data.edition.name}</p>}</div><RefreshButton fetching={query.isFetching} onRefresh={() => { void query.refetch() }} /></div></div>
      <QueryNotice pending={query.isPending} error={query.isError} hasData={!!data} notFound={query.error?.data?.code === 'NOT_FOUND'} onRetry={() => { void query.refetch() }} />
      {data && (
        <>
          <Card>
            <CardHeader>
              <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
                <div className="min-w-0"><h3 className="break-words text-lg font-semibold">{data.participant.username ? `@${data.participant.username}` : data.participant.name || data.participant.userId}</h3><p className="mt-1 break-all text-sm text-muted-foreground">{data.participant.email ?? '—'}</p><p className="mt-1 break-all font-mono text-xs text-muted-foreground">{data.participant.userId}</p><div className="mt-3"><EmailStatus status={data.participant.emailStatus} copy={copy} /><p className="mt-1 text-xs text-muted-foreground">{copy('currentEmailStatus')}</p></div></div>
                <div className="space-y-2 sm:text-right"><ParticipantStatus status={data.participant.status} copy={copy} /><p className="text-xs tabular-nums text-muted-foreground">{copy('registeredAt')} {utcDate(data.participant.registeredAt)} UTC</p><p className="text-xs tabular-nums text-muted-foreground">{copy('lastActivity')} {utcDate(data.participant.lastActivityAt)} UTC</p></div>
              </div>
            </CardHeader>
            <CardContent><div className="grid grid-cols-2 gap-3 md:grid-cols-4"><Metric label={copy('total')} value={<>{data.progress.completed}<span className="text-base font-normal text-muted-foreground"> / {data.progress.total}</span></>} detail={copy('remaining', { count: data.progress.remaining })} />{data.progress.categories.map(category => <Metric key={category.id} label={categoryLabel(category, copy)} value={<>{category.completed}<span className="text-base font-normal text-muted-foreground"> / {category.limit}</span></>} detail={copy('remaining', { count: category.remaining })} />)}<Metric label={copy('wins')} value={data.progress.wins} detail={`${copy('losses')} ${data.progress.losses} · ${copy('forfeits')} ${data.progress.forfeited}`} /></div></CardContent>
          </Card>
          {data.progress.current && <div className="flex flex-col gap-3 rounded-lg border border-border bg-muted/50 p-4 text-sm sm:flex-row sm:items-center sm:justify-between"><div className="min-w-0"><div className="mb-2 text-xs text-muted-foreground">{copy('currentGame')}</div><div className="flex flex-wrap items-center gap-3"><StatusBadge label={copy('active')} tone="active" /><span className="break-all font-mono text-xs">{data.progress.current.gameId}</span></div>{data.progress.current.retryLater && <p className="mt-2 text-xs text-muted-foreground">{copy('retryLater')}</p>}</div>{data.edition.lifecycle.competitionOpen && <p className="text-muted-foreground">{copy('finishRule')}</p>}</div>}
          <Card>
            <CardHeader><div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between"><div><h3 className="text-lg font-semibold">{copy('playerRecords')}</h3><p className="mt-1 text-sm text-muted-foreground">{copy('recordCount', { count: records.length })} · {copy('dateZone')}</p><p className="mt-1 text-xs text-muted-foreground">{copy('updated', { time: utcDate(data.serverNow) })}</p></div><Button variant="outline" asChild className="min-h-11"><InternalLink navigation={navigation} patch={{ participantId: null }} navigate={navigate}>{copy(data.edition.lifecycle.competitionOpen || data.edition.lifecycle.ended ? 'backProgress' : 'backRegistration')}</InternalLink></Button></div></CardHeader>
            <CardContent>{records.length ? <RecordRows records={records} expanded={expanded} toggle={toggle} copy={copy} /> : <p className="py-8 text-center text-sm text-muted-foreground">{copy('noGames')}</p>}<p className="mt-5 text-xs leading-relaxed text-muted-foreground">{copy('forfeitRule')} {copy('settledDefinition')}</p></CardContent>
          </Card>
        </>
      )}
    </div>
  )
}
